import { useCallback, useRef, useState } from 'react';
import { Button, Input, TextArea } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Pencil } from 'lucide-react';

import Can from '../guards/Can';

/** The pencil is always behind `<Can>`; a field without a permission is read only (fail closed). */
const EditGate = ({ permission, children }) =>
  permission ? <Can permission={permission}>{children}</Can> : null;

/**
 * Fail closed: the pencil exists only behind `<Can permission>`; a field without `permission` is
 * read only.
 *
 * Read view + pencil that turns into an input with Save / Cancel. Enter saves (single line),
 * Escape cancels. A required field rejects an empty value; a failed save keeps the editor open
 * with the API message.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {string} props.label Accessible name of the input.
 * @param {string} props.editLabel Accessible name of the pencil button.
 * @param {(value: string) => Promise<void>} props.onSave Rejects with the API message.
 * @param {boolean} [props.required]
 * @param {string} [props.requiredMessage] Error shown when a required field is empty.
 * @param {boolean} [props.multiline]
 * @param {string} props.permission Permission key that gates the pencil (`<Can>`); without it the field is read only.
 * @param {string} [props.emptyText] Shown in the read view when the value is empty.
 * @param {string} [props.valueClassName] Typography of the read view.
 * @param {import('react').ElementType} [props.as] Element of the read view (e.g. `h2`).
 */
function InlineEditField({
  value,
  label,
  editLabel,
  onSave,
  required = false,
  requiredMessage,
  multiline = false,
  permission,
  emptyText = '',
  valueClassName = 'text-sm text-content',
  as: ValueTag = 'span',
}) {
  const { t } = useTranslation('people-access');
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const startEditing = useCallback(() => {
    setDraft(value);
    setError(null);
    setIsEditing(true);
  }, [value]);

  const cancel = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const save = useCallback(async () => {
    if (savingRef.current) return;
    const next = draft.trim();
    if (required && !next) {
      setError(requiredMessage ?? t('groups.detail.nameRequired'));
      return;
    }
    if (next === (value ?? '').trim()) {
      cancel();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err?.message ?? t('groups.errors.generic'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [draft, required, requiredMessage, value, cancel, onSave, t]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        cancel();
      } else if (event.key === 'Enter' && !multiline) {
        event.preventDefault();
        save();
      }
    },
    [cancel, save, multiline],
  );

  const handleChange = useCallback((event) => {
    setDraft(event.target.value);
    setError(null);
  }, []);

  if (!isEditing) {
    return (
      <div className="flex items-start gap-2">
        <ValueTag className={value ? valueClassName : `${valueClassName} text-content-muted`}>
          {value || emptyText}
        </ValueTag>
        <EditGate permission={permission}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={Pencil}
            aria-label={editLabel}
            onClick={startEditing}
          />
        </EditGate>
      </div>
    );
  }

  const Field = multiline ? TextArea : Input;
  const actions = (
    <div className={`flex shrink-0 gap-2 ${multiline ? '' : 'h-[38px] items-center'}`}>
      <Button size="sm" loading={saving} onClick={save}>
        {t('groups.detail.save')}
      </Button>
      <Button variant="secondary" size="sm" disabled={saving} onClick={cancel}>
        {t('groups.detail.cancel')}
      </Button>
    </div>
  );
  const field = (
    <Field
      aria-label={label}
      value={draft}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      error={error ?? undefined}
      disabled={saving}
      autoFocus
    />
  );

  // One line: a compact input with Save / Cancel next to it. Several lines: full width, buttons below.
  if (!multiline) {
    return (
      <div className="flex flex-wrap items-start gap-2">
        <div className="w-72 max-w-full">{field}</div>
        {actions}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {field}
      {actions}
    </div>
  );
}

export default InlineEditField;
